import React, { useState } from 'react'
import { BriefingPack } from '../types'
import { Download, Share2, RotateCcw, Check } from 'lucide-react'

interface Props {
  briefingPack: BriefingPack
  childName: string
  onStartOver: () => void
}

export default function CompleteStep({ briefingPack, childName, onStartOver }: Props) {

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch('/api/briefing_packs/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken()
        },
        body: JSON.stringify({ briefing_pack: briefingPack })
      })

      if (!response.ok) throw new Error('Failed to generate PDF')

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${childName}-nanny-briefing-pack.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('PDF download error:', err)
      alert('Failed to download PDF. Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-emerald-50 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Your Briefing Pack is Ready
          </h2>
          <p className="text-gray-600 mt-2 max-w-md mx-auto">
            Download the PDF to keep a copy and share it with your nanny or prospective nannies.
          </p>
        </div>

        <div className="px-6 py-6 space-y-4">
          {/* Download Button */}
          <button
            onClick={handleDownloadPDF}
            className="w-full py-3 px-4 rounded-lg font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Download PDF
          </button>

        </div>
      </div>

      {/* Reminder about data */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Remember:</strong> We don't store your data. You need to download the PDF if you want to keep your briefing.
        </p>
      </div>

      {/* Start Over */}
      <div className="text-center">
        <button
          onClick={onStartOver}
          className="inline-flex items-center gap-2 py-2 px-4 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Start over
        </button>
      </div>
    </div>
  )
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}
